import { useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';

import { Layout } from '@data';
import { useCreateNewLayout, useToggle } from '@hooks';
import { activityAtom } from '@store';

import { LayoutFormData } from './NewLayoutForm';

export const LAYOUT_CREATE_MODE = {
    COPY_SELECTED: 'COPY_SELECTED',
    CREATE_NEW: 'CREATE_NEW',
} as const;

export type LayoutCreateMode = (typeof LAYOUT_CREATE_MODE)[keyof typeof LAYOUT_CREATE_MODE];

const CREATE_MODES = Object.values(LAYOUT_CREATE_MODE) as LayoutCreateMode[];

const checkMode = (mode: string): mode is LayoutCreateMode => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return CREATE_MODES.includes(mode as any);
};

type CreateLayoutHookReturns = {
    isLayoutSavePending: boolean;
    isAddingLayout: boolean;
    startAddingLayout: () => void;
    cancelAddingLayout: () => void;
    saveLayout: (data: LayoutFormData) => void;
    changeCreateMode: (mode: string) => void;
};

type LayoutFormMeta = { selectedLayoutId?: number };

export type LayoutFormPayload = LayoutFormData & LayoutFormMeta;

export const useCreateLayout = (
    setSelectedLayout: (layout: Layout) => void,
    selectedLayoutId?: number,
): CreateLayoutHookReturns => {
    const { mutateAsync: createNewLayout, isPending: isLayoutSavePending } = useCreateNewLayout();
    const [isAddingLayout, setIsAddingLayout] = useToggle();
    const [createMode, setCreateMode] = useState<LayoutCreateMode>(LAYOUT_CREATE_MODE.COPY_SELECTED);
    const activity = useAtomValue(activityAtom);

    const startAddingLayout = useCallback(() => {
        setIsAddingLayout(true);
    }, [setIsAddingLayout]);

    const cancelAddingLayout = useCallback(() => {
        setIsAddingLayout(false);
    }, [setIsAddingLayout]);

    const saveLayout = useCallback(
        async ({ name: layoutName }: LayoutFormData) => {
            const sourceLayoutId = createMode === LAYOUT_CREATE_MODE.COPY_SELECTED ? selectedLayoutId : undefined;
            const layout = await createNewLayout({
                name: layoutName,
                sourceLayoutId,
                clubTypeId: activity?.club?.clubTypeId ?? undefined,
            });
            if (!layout) {
                throw new Error('Failed to create layout');
            }
            setSelectedLayout(layout);
            setIsAddingLayout(false);
        },
        [
            createMode,
            selectedLayoutId,
            createNewLayout,
            activity?.club?.clubTypeId,
            setSelectedLayout,
            setIsAddingLayout,
        ],
    );

    const changeCreateMode = useCallback((mode: string) => {
        if (checkMode(mode)) {
            setCreateMode(mode);
        }
    }, []);

    return {
        isLayoutSavePending,
        isAddingLayout,
        startAddingLayout,
        cancelAddingLayout,
        saveLayout,
        changeCreateMode,
    };
};
